/**
 * Deterministic collage-mask avatars. Never faces: each agent gets a cut-out
 * mask (paper bag, balaclava, domino, gas mask, TV head, welding visor) glued
 * on torn paper scraps with halftone, tape and a sticker, all chosen by a
 * PRNG seeded from the agent's avatar_seed (or id). Same seed → same SVG.
 */

export type MaskKind = "torba" | "kar-maskesi" | "domino" | "gaz-maskesi" | "ekran" | "vizor";
export const MASK_KINDS: readonly MaskKind[] = ["torba", "kar-maskesi", "domino", "gaz-maskesi", "ekran", "vizor"];

export const NEON = ["#ff2bd6", "#e8ff2a", "#2b6bff", "#39ff14", "#ff3131", "#00e5ff"] as const;
export const TAM = ["#ece6da", "#d7263d", "#8a8478", "#2a2a2c"] as const;

export interface Scrap {
  points: string;
  fill: string;
}

export interface AvatarSpec {
  ring: string;
  background: string;
  scraps: Scrap[];
  halftone: string;
  mask: MaskKind;
  maskFill: string;
  eyeFill: string;
  tilt: number;
  tape: { x: number; y: number; angle: number };
  sticker: { kind: "yildiz" | "simsek" | "nokta"; x: number; y: number; fill: string };
}

/** FNV-1a 32-bit. */
export function hashSeed(seed: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** mulberry32 */
export function prng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rnd: () => number, list: readonly T[]): T {
  return list[Math.floor(rnd() * list.length) % list.length] as T;
}

function tornPolygon(rnd: () => number, cx: number, cy: number, r: number): string {
  const n = 7 + Math.floor(rnd() * 5);
  const pts: string[] = [];
  for (let i = 0; i < n; i++) {
    const a = (i / n) * Math.PI * 2 + rnd() * 0.4;
    const rr = r * (0.6 + rnd() * 0.5);
    pts.push(`${(cx + Math.cos(a) * rr).toFixed(1)},${(cy + Math.sin(a) * rr).toFixed(1)}`);
  }
  return pts.join(" ");
}

export function avatarSpec(seed: string, theme: "neon" | "tam" = "neon"): AvatarSpec {
  const rnd = prng(hashSeed(seed));
  const palette: readonly string[] = theme === "tam" ? TAM : NEON;
  const ring = theme === "tam" ? pick(rnd, ["#d7263d", "#ece6da"]) : pick(rnd, NEON);
  const scraps: Scrap[] = Array.from({ length: 3 }, () => ({
    points: tornPolygon(rnd, 15 + rnd() * 70, 15 + rnd() * 70, 26 + rnd() * 20),
    fill: pick(rnd, palette),
  }));
  const mask = pick(rnd, MASK_KINDS);
  return {
    ring,
    background: theme === "tam" ? "#0b0b0c" : pick(rnd, ["#14141a", "#1d1033", "#0d1f2d", "#231a0b"]),
    scraps,
    halftone: theme === "tam" ? "#ece6da" : pick(rnd, palette),
    mask,
    maskFill: theme === "tam" ? pick(rnd, ["#ece6da", "#1c1c1e"]) : pick(rnd, ["#f4efe2", "#000000", "#c9b48a", ...palette]),
    eyeFill: theme === "tam" ? "#d7263d" : pick(rnd, ["#000000", "#39ff14", "#ff2bd6", "#e8ff2a"]),
    tilt: Math.round((rnd() - 0.5) * 24),
    tape: { x: 10 + rnd() * 50, y: 4 + rnd() * 10, angle: Math.round((rnd() - 0.5) * 70) },
    sticker: {
      kind: pick(rnd, ["yildiz", "simsek", "nokta"] as const),
      x: rnd() < 0.5 ? 74 : 8,
      y: rnd() < 0.5 ? 74 : 12,
      fill: pick(rnd, palette),
    },
  };
}
